import { useRef, useEffect, useCallback } from 'react';
import { sendProgressEvents } from './reader.api';
import type { ProgressEvent } from '@bookclub/shared';

const FLUSH_INTERVAL_MS = 5000;
const SCROLL_THROTTLE_MS = 2000;

interface UseProgressTrackerOptions {
  chapterId: string;
  enabled: boolean;
}

interface ScrollSnapshot {
  scrollDepth: number;
  lastPosition: number;
}

/**
 * Tracks scroll depth, active time, video progress, and exit events.
 * Batches and flushes events every 5 seconds, and on exit.
 */
export function useProgressTracker({ chapterId, enabled }: UseProgressTrackerOptions) {
  const eventBuffer = useRef<ProgressEvent[]>([]);
  const lastScrollTime = useRef<number>(0);
  const activeStartTime = useRef<number | null>(null);
  const isActive = useRef(true);
  const lastScrollSnapshot = useRef<ScrollSnapshot | null>(null);

  const makeEventId = useCallback(
    (type: string) => `${type}-${chapterId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    [chapterId],
  );

  // Flush buffer to API
  const flush = useCallback(
    async (extra?: ProgressEvent[]) => {
      const events = [...eventBuffer.current, ...(extra ?? [])];
      if (events.length === 0) return;
      eventBuffer.current = [];

      try {
        await sendProgressEvents({ events });
      } catch {
        // Network errors must not break reading UX — silently discard
      }
    },
    [],
  );

  const addEvent = useCallback((event: ProgressEvent) => {
    eventBuffer.current.push(event);
  }, []);

  const buildScrollEvent = useCallback(
    (eventIdSuffix: string, snapshot: ScrollSnapshot): ProgressEvent => ({
      eventId: makeEventId(`scroll-${eventIdSuffix}`),
      type: 'scroll',
      chapterId,
      payload: {
        scrollDepth: snapshot.scrollDepth,
        lastPosition: snapshot.lastPosition,
      },
      clientTs: new Date().toISOString(),
    }),
    [chapterId, makeEventId],
  );

  // Active time counter — counts seconds while page is visible
  useEffect(() => {
    if (!enabled) return;

    const startTick = () => {
      activeStartTime.current = Date.now();
      isActive.current = true;
    };

    const stopTick = () => {
      if (activeStartTime.current !== null && isActive.current) {
        const delta = Math.round((Date.now() - activeStartTime.current) / 1000);
        if (delta > 0) {
          addEvent({
            eventId: makeEventId('time'),
            type: 'active_time',
            chapterId,
            payload: { deltaSeconds: delta },
            clientTs: new Date().toISOString(),
          });
        }
        activeStartTime.current = null;
        isActive.current = false;
      }
    };

    startTick();

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopTick();
        const snapshot = lastScrollSnapshot.current;
        if (snapshot) {
          void flush([buildScrollEvent('hidden', snapshot)]);
        }
      } else {
        startTick();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      stopTick();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, enabled]);

  // Periodic flush every 5s
  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(flush, FLUSH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, flush]);

  // Send exit event and flush on unmount / route change
  useEffect(() => {
    if (!enabled) return;

    return () => {
      // Capture active time before exit
      if (activeStartTime.current !== null) {
        const delta = Math.round((Date.now() - activeStartTime.current) / 1000);
        if (delta > 0) {
          eventBuffer.current.push({
            eventId: makeEventId('time-exit'),
            type: 'active_time',
            chapterId,
            payload: { deltaSeconds: delta },
            clientTs: new Date().toISOString(),
          });
        }
      }

      const snapshot = lastScrollSnapshot.current;
      if (snapshot) {
        eventBuffer.current.push(buildScrollEvent('exit', snapshot));
      }

      const exitEvent: ProgressEvent = {
        eventId: makeEventId('exit'),
        type: 'chapter_exit',
        chapterId,
        payload: {},
        clientTs: new Date().toISOString(),
      };

      // Fire-and-forget flush with exit event
      void flush([exitEvent]);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chapterId, enabled]);

  // Scroll tracker — call this from reader's onScroll handler
  const trackScroll = useCallback(
    (scrollableEl: HTMLElement | null) => {
      if (!enabled || !scrollableEl) return;

      const now = Date.now();
      if (now - lastScrollTime.current < SCROLL_THROTTLE_MS) return;
      lastScrollTime.current = now;

      const { scrollTop, scrollHeight, clientHeight } = scrollableEl;
      const scrollableDistance = scrollHeight - clientHeight;
      if (scrollableDistance <= 0) return;

      const depth = Math.min(1, (scrollTop + clientHeight) / scrollHeight);
      const lastPosition = Math.min(1, Math.max(0, scrollTop / scrollableDistance));
      const snapshot = {
        scrollDepth: Math.round(depth * 1000) / 1000,
        lastPosition: Math.round(lastPosition * 1000) / 1000,
      };
      lastScrollSnapshot.current = snapshot;

      addEvent(buildScrollEvent('throttled', snapshot));
    },
    [enabled, addEvent, buildScrollEvent],
  );

  // Video progress tracker
  const trackVideoProgress = useCallback(
    (blockId: string, positionSeconds: number, durationSeconds: number) => {
      if (!enabled) return;
      addEvent({
        eventId: makeEventId(`video-${blockId}`),
        type: 'video_progress',
        chapterId,
        payload: {
          blockId,
          positionSeconds: Math.round(positionSeconds),
          durationSeconds: Math.round(durationSeconds),
        },
        clientTs: new Date().toISOString(),
      });
    },
    [chapterId, enabled, addEvent, makeEventId],
  );

  const trackVideoEnded = useCallback(
    (blockId: string, positionSeconds: number, durationSeconds: number) => {
      if (!enabled) return;
      addEvent({
        eventId: makeEventId(`video-ended-${blockId}`),
        type: 'video_progress',
        chapterId,
        payload: {
          blockId,
          positionSeconds: Math.round(positionSeconds),
          durationSeconds: Math.round(durationSeconds),
          completed: true,
        },
        clientTs: new Date().toISOString(),
      });
    },
    [chapterId, enabled, addEvent, makeEventId],
  );

  return { trackScroll, trackVideoProgress, trackVideoEnded };
}
